import {
  WbSensitiveKeyValueField,
  customAlertApiRef,
} from '@agilelab/plugin-wb-platform';
import { handleFailedResponse } from '@agilelab/plugin-wb-platform-common';
import {
  AZURE_DEVOPS_PROVIDER,
  getAzureDevOpsTokenKey,
} from '@agilelab/plugin-wb-user-config-client';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@mainstage/core-plugin-api';
import { CircularProgress } from '@material-ui/core';
import { ReactElement, useEffect, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { z } from 'zod';
import { userConfigApiRef } from './api';

const validator = z.object({
  key: z.string(),
  value: z.string().trim().min(1, 'The token cannot be empty'),
  isSensitive: z.boolean(),
});

export function AzureDevOpsOrganizationTokens({
  entityRef,
  disabled = false,
  onEditModeChange,
}: {
  entityRef?: string;
  disabled?: boolean;
  onEditModeChange?: (key?: string) => void;
}): ReactElement {
  const userConfigApi = useApi(userConfigApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [version, setVersion] = useState(0);
  const [editingKey, setEditingKey] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const {
    value: configs = [],
    loading,
    error,
  } = useAsync(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('wb-configuration');
    const response = await fetch(`${baseUrl}/git-integrations`);
    await handleFailedResponse(response);
    const integrations: {
      azure?: {
        host: string;
        organizations?: string[];
        credentials?: { organizations?: string[] }[];
      };
    } = await response.json();
    const { token } = await identityApi.getCredentials();
    const allConfigs = await userConfigApi.getAllUserConfigs({
      token: token!,
      entityRef,
      redactWith: '[REDACTED]',
    });
    const scopes = new Map<string, string>();
    const azure = integrations[AZURE_DEVOPS_PROVIDER];
    if (azure) {
      [
        ...(azure.organizations ?? []),
        ...(azure.credentials ?? []).flatMap(
          credential => credential.organizations ?? [],
        ),
      ].forEach(organization => {
        const name = organization.trim();
        if (name) scopes.set(getAzureDevOpsTokenKey(name), name);
      });
    }
    return Array.from(scopes, ([key, organization]) => ({
      organization,
      key,
      value: allConfigs.find(config => config.key === key)?.value ?? '',
      isSensitive: true,
    }));
  }, [userConfigApi, identityApi, discoveryApi, fetch, entityRef, version]);

  useEffect(() => {
    if (error) alertApi.post({ error, severity: 'error' });
  }, [alertApi, error]);

  const edit = (key?: string) => {
    setEditingKey(key);
    onEditModeChange?.(key);
  };

  const mutate = async (action: (token: string) => Promise<unknown>) => {
    setIsSaving(true);
    try {
      const { token } = await identityApi.getCredentials();
      await action(token!);
      edit(undefined);
      setVersion(previous => previous + 1);
      return true;
    } catch (mutationError) {
      alertApi.post({ error: mutationError, severity: 'error' });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          {configs.map(config => {
            return (
              <WbSensitiveKeyValueField
                key={`${config.key}:${version}`}
                configName={`Organization-scoped access token (${config.organization})`}
                keyHelperText="Overrides the global token for this organization. Leave unset to use global or permitted platform credentials."
                valueHelperText="Personal Access Token"
                valuePlaceholder="Personal Access Token"
                active={editingKey === config.key}
                editableKey={false}
                forceSensitiveValue
                initialValue={config}
                validator={validator}
                actionsEnabled={
                  !disabled && !error && !isSaving && editingKey === undefined
                }
                isSaving={isSaving}
                onStartEditMode={() => edit(config.key)}
                onCancelEditMode={() => edit(undefined)}
                onSaveAction={updated =>
                  mutate(token =>
                    userConfigApi.storeUserConfig({
                      key: config.key,
                      value: updated.value,
                      isSensitive: true,
                      token,
                      entityRef,
                    }),
                  )
                }
                deleteDialogTitle="Delete Azure DevOps organization token"
                onDeleteAction={async () => {
                  edit(config.key);
                  await mutate(token =>
                    userConfigApi.deleteUserConfig({
                      key: config.key,
                      token,
                      entityRef,
                    }),
                  );
                  edit(undefined);
                }}
              />
            );
          })}
        </>
      )}
    </>
  );
}
